import { defineStore } from 'pinia'
import { apiClient } from '@/shared/api/client'
import { login as apiLogin, logout as apiLogout } from '@/shared/api/auth'
import { useAgentStatusStore } from '@/shared/stores/agent_status'

export type UserRole = 'owner' | 'admin' | 'team_admin' | 'agent'

export interface User {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  role: UserRole
  /** URL relative de la photo de profil (/media/avatars/…) ou null. */
  avatar: string | null
  team_id: number | null
  team_name: string | null
  phone_extension: string
  sip_password: string
  sip_host: string
  sip_ws_path: string
  outbound_caller_id: string
  /** Fuseau horaire du compte (IANA, ex. 'America/Montreal'). Vide = global. */
  timezone: string
  /** Droit de télécharger l'audio des enregistrements (accordé par un superviseur). */
  can_download_recordings: boolean
  /** Droit d'envoyer ses RDV validés vers PexiLine, résolu serveur
   *  (superviseur+ toujours ; agent si un admin l'a accordé). */
  can_send_to_labyrdv: boolean
  /** Accès Liste qualifiée, déjà résolu par le serveur (owner → true ;
   *  admin → selon l'octroi du owner ; autres rôles → false). */
  qualified_list_access: boolean
  /** Gestion des plannings, résolu par le serveur (même règle d'octroi). */
  planning_access: boolean
  /** « Mode agent » d'un superviseur : reçoit les appels comme un agent tant
   *  qu'il est actif. Toujours false pour un agent (il l'est déjà). */
  agent_mode: boolean
  /** Nombre de « lignes » (trunks sortants) assignées. > 1 ⇒ page Lignes visible. */
  lines_count: number
  /** Ligne (trunk sortant) actuellement choisie par l'agent, ou null. */
  active_trunk_id: number | null
  active_trunk_name: string | null
  tenant_id: number
  is_staff: boolean
  is_active: boolean
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null as User | null,
    initialized: false,
  }),

  getters: {
    isAuthenticated: (state) => !!state.user,

    displayName: (state) =>
      state.user
        ? state.user.first_name && state.user.last_name
          ? `${state.user.first_name} ${state.user.last_name}`
          : state.user.username
        : '',

    // Role helpers
    isOwner: (state) => state.user?.role === 'owner',
    isAdmin: (state) => state.user?.role === 'owner' || state.user?.role === 'admin',
    isTeamAdmin: (state) =>
      ['owner', 'admin', 'team_admin'].includes(state.user?.role ?? ''),
    isAgent: (state) => state.user?.role === 'agent',
    /** Peut envoyer des RDV vers PexiLine (LabyRDV) : superviseur+, ou agent
     *  à qui un admin l'a accordé (résolu serveur dans /me). */
    canSendToLabyrdv: (state) =>
      ['owner', 'admin', 'team_admin'].includes(state.user?.role ?? '') ||
      state.user?.can_send_to_labyrdv === true,
    /** Le compte est-il un superviseur autorisé à activer le « mode agent » ? */
    canUseAgentMode: (state) =>
      ['owner', 'admin', 'team_admin'].includes(state.user?.role ?? ''),
    /** Agit-il actuellement comme un agent (agent, ou superviseur en mode agent) ? */
    actingAsAgent: (state) =>
      state.user?.role === 'agent' || state.user?.agent_mode === true,

    // Legacy compat — keeps existing is_staff checks working
    isStaff: (state) => state.user?.is_staff === true,

    /** Peut voir la Liste qualifiée (droit résolu côté serveur). */
    canSeeQualifiedList: (state) => state.user?.qualified_list_access === true,

    /** Peut gérer les plannings et valider les heures (droit résolu serveur). */
    canManagePlanning: (state) => state.user?.planning_access === true,

    // What this user can do
    canManageUsers: (state) =>
      ['owner', 'admin', 'team_admin'].includes(state.user?.role ?? ''),
    canManageAdmins: (state) => state.user?.role === 'owner',
    canCreateTeamAdmin: (state) =>
      state.user?.role === 'owner' || state.user?.role === 'admin',
    canSeeAllAgents: (state) =>
      state.user?.role === 'owner' || state.user?.role === 'admin',

    roleBadge: (state): { label: string; color: string } => {
      const map: Record<UserRole, { label: string; color: string }> = {
        owner: { label: 'Propriétaire', color: '#6366F1' },
        admin: { label: 'Administrateur', color: '#0284C7' },
        team_admin: { label: 'Superviseur', color: '#D97706' },
        agent: { label: 'Agent', color: '#16A34A' },
      }
      return map[state.user?.role ?? 'agent'] ?? map.agent
    },
  },

  actions: {
    async login(username: string, password: string) {
      await apiLogin(username, password)
      await this.loadProfile()
    },

    logout() {
      // Teardown CENTRALISÉ (point de sortie unique : menu App.vue,
      // SettingsView et — via stopAutoTransitions — l'éviction de session dans
      // l'intercepteur). Arrête les timers d'auto-transition, dont le heartbeat
      // de CONNEXION inconditionnel qui, sinon, continuerait de POSTer des 401
      // en boucle après invalidation du token. Le passage offline part en
      // best-effort tant que le token est encore valide (le sweep serveur
      // reste le filet si la requête ne part pas à temps).
      try {
        const agent = useAgentStatusStore()
        agent.change('offline').catch(() => { /* best-effort */ })
        agent.stopAutoTransitions()
      } catch { /* store/pinia indisponible — on n'empêche jamais le logout */ }
      apiLogout()
      this.user = null
      // On NE remet PAS `initialized` à false : l'état d'auth est connu
      // (déconnecté). Le remettre à false forçait la garde du routeur à
      // relancer un GET /me/ (401) pendant la navigation vers /login → la
      // navigation ne se résolvait qu'après ce round-trip, prolongeant le
      // flash de la page précédente. La reconnexion appelle loadProfile().
    },

    async loadProfile() {
      try {
        const r = await apiClient.get('/api/auth/me/')
        this.user = r.data
      } catch {
        this.user = null
      } finally {
        this.initialized = true
      }
    },

    /** Active/désactive le « mode agent » (superviseur). Met à jour le profil
     *  local pour que toute l'UI réagisse immédiatement. */
    async setAgentMode(enabled: boolean) {
      const r = await apiClient.post('/api/pexitalk/accounts/agent-mode/', { enabled })
      if (this.user) this.user.agent_mode = !!r.data.agent_mode
      return this.user?.agent_mode ?? false
    },
  },
})
